import React, { useEffect, useState } from "react";
import { Card, Badge, Alert, Spinner, Table } from "react-bootstrap";
import { fetchClientsEnLigne } from "./DashboardService";

/**
 * Qui est sur le site client en ce moment.
 *
 * Le dashboard savait qui s'était connecté un jour, jamais qui est là
 * maintenant : une connexion à 9 h ne prouve pas une présence à 18 h.
 *
 * Le nombre seul n'appellerait aucune action — « 3 clients en ligne », et
 * après ? Les noms, si : on peut décrocher pendant que la personne est
 * encore devant son écran.
 *
 * Est compté un client actif dans les 5 dernières minutes. Les livreurs sont
 * exclus côté serveur : leur application envoie sa position en continu, ils
 * afficheraient un plancher permanent au lieu d'un signal. Les admins aussi,
 * sans quoi on se compterait soi-même en regardant cet écran.
 */

const RAFRAICHISSEMENT_MS = 30000;

const LIBELLES_ROLE = {
  1: "Particulier",
  2: "Entreprise",
  6: "Fournisseur",
};

/**
 * Navigateur et plate-forme en une seule mention, et seulement ce qu'on sait.
 * « Inconnu · Inconnu » n'apprend rien : on prefere un tiret.
 */
const machine = (c) => {
  const parts = [c.navigateur, c.plateforme].filter(
    (v) => v && v !== "Inconnu"
  );
  return parts.length > 0 ? parts.join(" · ") : null;
};

const depuis = (minutes) => {
  if (minutes === null || minutes === undefined) return null;
  if (minutes <= 0) return "à l'instant";
  return `il y a ${minutes} min`;
};

const ClientsEnLigne = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let annule = false;

    const charger = async () => {
      try {
        const resultat = await fetchClientsEnLigne();
        if (!annule) {
          setData(resultat);
          setError(null);
        }
      } catch (e) {
        console.error("Erreur chargement clients en ligne:", e);
        if (!annule) setError("Impossible de charger les clients en ligne");
      } finally {
        if (!annule) setLoading(false);
      }
    };

    charger();
    const interval = setInterval(charger, RAFRAICHISSEMENT_MS);

    return () => {
      annule = true;
      clearInterval(interval);
    };
  }, []);

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="warning">{error}</Alert>;
  }

  const clients = data?.clients ?? [];
  const total = data?.total ?? 0;

  return (
    <Card className={total > 0 ? "border-success" : ""}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span className="fw-bold">
          {/* Le point vert ne s'allume que s'il y a vraiment quelqu'un : un
              indicateur toujours au vert ne se regarde plus. */}
          <span
            className="d-inline-block rounded-circle me-2"
            style={{
              width: 9,
              height: 9,
              backgroundColor: total > 0 ? "#198754" : "#adb5bd",
            }}
          />
          {total === 0
            ? "Personne sur le site"
            : `${total} client${total > 1 ? "s" : ""} sur le site`}
        </span>
        <Badge bg="light" text="dark">
          {data?.minutes ?? 5} dernières minutes
        </Badge>
      </Card.Header>

      <Card.Body className="pb-2">
        {/* Le carton reste affiché même vide : zéro client en ligne est une
            information, et un carton qui disparaît laisse croire à une panne. */}
        {total === 0 ? (
          <p className="text-muted mb-2">
            Aucune activité sur Marion Livraison en ce moment.
          </p>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="mb-2 align-middle">
              <thead>
                <tr className="text-muted small">
                  <th>Client</th>
                  <th>Page</th>
                  <th>Navigateur</th>
                  <th>IP</th>
                  <th className="text-end">Vu</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <a
                        href={`/user/edit/${c.id}`}
                        className="text-decoration-none fw-semibold"
                        title={c.email}
                      >
                        {c.nom}
                      </a>
                      {/* La société en plus du nom, quand le compte en a une. */}
                      {c.societe && (
                        <span className="text-muted small"> · {c.societe}</span>
                      )}
                      <div className="text-muted small">
                        {LIBELLES_ROLE[c.role] ?? "—"}
                      </div>
                    </td>
                    {/* Le chemin brut, sans le traduire en libelle : une table de
                        correspondance des routes derive des qu'une route change,
                        et un chemin est deja lisible. */}
                    <td>
                      {c.page ? (
                        <code className="small">{c.page}</code>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="small">
                      {machine(c) ?? <span className="text-muted">—</span>}
                    </td>
                    <td className="small text-muted font-monospace">
                      {c.ip ?? "—"}
                    </td>
                    <td className="text-end small text-muted text-nowrap">
                      {depuis(c.minutes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {/* Le verrou d'ecriture du serveur est d'une minute : ces trois
                colonnes decrivent donc une requete de la derniere minute, pas
                l'instant present. A savoir avant de conclure qu'un client est
                « reste » sur une page. */}
            <p className="text-muted small mb-2">
              Page, navigateur et IP datent de la dernière requête enregistrée,
              à une minute près.
            </p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ClientsEnLigne;
